import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './checkout.css';
import firebase from '../../firebase-config';
import { firestoreService } from '../../services/firestoreService';
import { API_URL } from '../../config/apiConfig';
import { EXCHANGE_RATES, CURRENCY_SYMBOLS, formatPrice } from '../../config/currencyConfig';
import { trackEvent } from '../../config/googleAnalytics';

const PLAN_HIERARCHY = {
  weekly: 1,
  monthly: 2,
  yearly: 3
};

const PLAN_DURATIONS = {
  weekly: {
    days: 7,
    label: '1 Week'
  },
  monthly: {
    days: 30,
    label: '1 Month'
  },
  yearly: {
    days: 365,
    label: '1 Year'
  }
};

const Checkout = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [planDetails, setPlanDetails] = useState(null);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  
  useEffect(() => {
    const initializeCheckout = async () => {
      try {
        // Check if user is logged in first
        const user = firebase.auth.currentUser;
        if (!user) {
          localStorage.setItem('redirectAfterLogin', '/checkout');
          navigate('/login');
          return;
        }

        // Get plan from localStorage
        const selectedPlan = localStorage.getItem('selectedPlan');
        if (!selectedPlan) {
          navigate('/pricing');
          return;
        }

        // For new users, we don't need to check existing subscription
        // Just set up the payment
        const parsedPlan = JSON.parse(selectedPlan);
        setPlanDetails(parsedPlan);
        
        // Track checkout page view with plan details
        trackEvent(
          'Ecommerce', 
          'Checkout View', 
          parsedPlan.type,
          parsedPlan.price
        );

        // Load Yoco SDK
        if (typeof window.YocoSDK === 'undefined') {
          const script = document.createElement('script');
          script.src = 'https://js.yoco.com/sdk/v1/yoco-sdk-web.js';
          script.async = true;
          script.onload = () => setSdkReady(true);
          document.body.appendChild(script);
        } else {
          setSdkReady(true);
        }
      } catch (error) {
        console.error('Initialization error:', error);
        setError('Failed to initialize payment. Please try again.');
      }
    };

    initializeCheckout();
  }, [navigate]);

  const isUpgrade = currentSubscription && 
    PLAN_HIERARCHY[planDetails?.type] > PLAN_HIERARCHY[currentSubscription?.type];

  const createSubscriptionObject = (paymentResult, user) => {
    const now = new Date();
    const duration = PLAN_DURATIONS[planDetails.type].days * 24 * 60 * 60 * 1000;
    
    return {
      isActive: true,
      type: planDetails.type,
      startDate: now.toISOString(),
      expiryDate: new Date(now.getTime() + duration).toISOString(),
      paymentId: paymentResult.id,
      amount: planDetails.price,
      currency: 'ZAR',
      lastPaymentDate: now.toISOString(),
      paymentStatus: 'completed',
      userDetails: {
        uid: user.uid,
        email: user.email,
        name: user.displayName,
        photoURL: user.photoURL
      },
      paymentDetails: {
        transactionId: paymentResult.id,
        amount: planDetails.price,
        currency: 'ZAR',
        paymentMethod: 'yoco',
        paymentDate: now.toISOString()
      }
    };
  };

  const handlePayment = async () => {
    if (loading || !planDetails || !sdkReady) return;
    
    setLoading(true);
    setError('');

    try {
      const user = firebase.auth.currentUser;
      if (!user) {
        throw new Error('Please sign in to continue');
      }

      // Track payment initiation
      trackEvent(
        'Ecommerce',
        'Payment Initiated',
        planDetails.type,
        planDetails.price
      );

      // Skip server API call and use direct Yoco integration
      console.log('Using direct Yoco integration with live keys');
      
      // Convert price back to ZAR for payment processing if needed
      const priceInZAR = planDetails.currency !== 'ZAR' 
        ? Math.round(planDetails.price / EXCHANGE_RATES[planDetails.currency]) 
        : planDetails.price;
      
      console.log(`Converting ${formatPrice(planDetails.price, planDetails.currency)} to ZAR: R${priceInZAR}`);
      
      // Create direct session with live key
      const directSession = {
        id: 'direct_session_' + Date.now(),
        publicKey: 'pk_live_40625e75bVyGRYGd7fb4' // Always use live key
      };
      
      setupYocoPayment(directSession, user, priceInZAR);
    } catch (error) {
      console.error('Payment error:', error);
      
      // Track payment error
      trackEvent(
        'Ecommerce',
        'Payment Error',
        error.message || 'Unknown error'
      );
      
      setError(error.message || 'An error occurred during payment processing');
      setLoading(false);
    }
  };

  const setupYocoPayment = (session, user, priceInZAR) => {
    // Always use live key and environment for real payments
    const publicKey = 'pk_live_40625e75bVyGRYGd7fb4'; // Your live key
    const environment = 'live';
    
    console.log(`Using Yoco ${environment} environment with key: ${publicKey}`);

    // Initialize Yoco SDK with live keys
    const yoco = new window.YocoSDK({
      publicKey: publicKey,
      environment: environment,
      locale: 'en',
      style: {
        base: {
          color: '#32325d',
          fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
          fontSmoothing: 'antialiased',
          fontSize: '16px',
          '::placeholder': {
            color: '#aab7c4'
          }
        },
        invalid: {
          color: '#fa755a',
          iconColor: '#fa755a'
        }
      }
    });

    // Show Yoco inline form with ZAR amount
    yoco.showPopup({
      amountInCents: Math.round(priceInZAR * 100),
      currency: 'ZAR', // Always use ZAR for Yoco
      name: 'RizzologyGPT Subscription',
      description: `${planDetails.type} Plan`,
      callback: async function (result) {
        if (result.error) {
          console.error('Payment error:', result.error);
          
          // Track payment failure
          trackEvent(
            'Ecommerce',
            'Payment Failed',
            result.error.message || 'Card declined'
          );
          
          let errorMessage = 'Payment failed: ';
          if (result.error.message.includes('card')) {
            errorMessage += 'Please check your card details and try again.';
          } else {
            errorMessage += result.error.message;
          }
          setError(errorMessage);
          setLoading(false);
          return;
        }

        try {
          const subscription = createSubscriptionObject(result, user);

          // Track successful payment
          trackEvent(
            'Ecommerce',
            'Payment Completed',
            planDetails.type,
            planDetails.price
          );

          // Save to Firebase
          await firestoreService.saveSubscription(user.uid, subscription, user.email);
          
          // Update local storage
          localStorage.setItem('userSubscription', JSON.stringify(subscription));
          localStorage.removeItem('selectedPlan');
          localStorage.removeItem('redirectAfterLogin');
          
          // Navigate to chat
          navigate('/chat');
        } catch (error) {
          console.error('Firebase save error:', error);
          
          // Track subscription save error
          trackEvent(
            'Ecommerce',
            'Subscription Save Error',
            error.message || 'Database error'
          );
          
          setError(`Payment successful but subscription save failed. Please contact support with your payment ID: ${result.id}`);
          setLoading(false);
        }
      }
    });
  };

  const handleStartPayment = () => {
    if (currentSubscription?.isActive) {
      if (!isUpgrade) {
        localStorage.removeItem('selectedPlan');
        navigate('/chat');
        return;
      }
    }
    handlePayment();
  };

  if (!planDetails || !sdkReady) {
    return (
      <div className="checkout-container">
        <div className="checkout-card">
          <h2>Initializing Payment</h2>
          <div className="loading-message">
            Please wait while we prepare your payment...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-container">
      <div className="checkout-card">
        <h2>Complete Your Purchase</h2>
        
        {currentSubscription?.isActive && (
          <div className="subscription-notice">
            {isUpgrade ? (
              <div className="upgrade-notice">
                <h3>Upgrade Your Plan</h3>
                <p>You currently have the {currentSubscription.type} plan.</p>
                <p>Upgrading to {planDetails.type} will give you access to more features!</p>
              </div>
            ) : (
              <div className="active-subscription-notice">
                <h3>Active Subscription</h3>
                <p>You already have an active {currentSubscription.type} plan.</p>
                <p>This plan is valid until {new Date(currentSubscription.expiryDate).toLocaleDateString()}</p>
                <button 
                  className="return-to-chat-button"
                  onClick={() => {
                    // Clear the selected plan before navigating
                    localStorage.removeItem('selectedPlan');
                    navigate('/chat');
                  }}
                >
                  Return to Chat
                </button>
              </div>
            )}
          </div>
        )}

        <div className="plan-summary">
          <div className="plan-header">
            <h3>{planDetails.type.charAt(0).toUpperCase() + planDetails.type.slice(1)} Plan</h3>
            <span className="plan-price">
              {planDetails.currency === 'ZAR' 
                ? `R ${planDetails.price}` 
                : `${CURRENCY_SYMBOLS[planDetails.currency] || ''}${planDetails.price}`}
            </span>
          </div>

          <div className="plan-features">
            <li>Full access to RizzologyGPT AI</li>
            <li>Unlimited AI conversations</li>
            <li>24/7 AI availability</li>
            <li>Message analysis & suggestions</li>
            <li>Conversation starters</li>
            <li>Response timing optimization</li>
            <li>Personality insights</li>
            <li>Priority support</li>
            <li className="duration-highlight">
              Duration: {PLAN_DURATIONS[planDetails.type].label}
            </li>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        
        {(!currentSubscription?.isActive || isUpgrade) && (
          <button 
            onClick={handleStartPayment}
            className="checkout-button"
            disabled={loading}
          >
            {loading ? 'Processing...' : planDetails.currency === 'ZAR' 
              ? `Pay R${planDetails.price}` 
              : `Pay ${CURRENCY_SYMBOLS[planDetails.currency] || ''}${planDetails.price}`}
          </button>
        )}

        <div className="secure-payment">
          <span className="secure-icon">🔒</span>
          Secure payment powered by Yoco
        </div>
      </div>
    </div>
  );
};

export default Checkout; 